import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing4, Spacing12 } from 'autogen/design-tokens/tokens';
import type { ChoosableSubscriptionFrequencyGroup, SubscriptionFrequencyInfo } from 'autogen/swagger/all';
import {
    DefaultIntervals,
    Every,
    Frequency,
    FrequencyExplanation
} from 'autogen/translation-keys/trans-website-product-subscription';
import InlineButton from 'components/generic/InlineButton/InlineButton';
import { Typography } from 'components/generic/typography';
import { SelectedFrequencyContext } from 'components/pages/PDP/components/MidSection/hooks/context';
import type { IOption } from 'components/shared/inputs/select';
import Select from 'components/shared/inputs/select/Select';
import { format } from 'modules/helpers/strings';
import { getFrequencyFromId, getSelectedGroup, getSelectedItem, mapOptionsData } from './shared/helpers';

const Wrapper = styled.div``;

const SelectionWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${Spacing12};
    margin-bottom: ${Spacing12};
`;

const SelectionContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing4};
`;

const StyledSelect = styled(Select)`
    width: 100%;
`;

const SubLine = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Explainer = styled(Typography.Body)`
    margin-bottom: 0;
    margin-right: ${Spacing12};
`;

function getFormattedGroupOptions(choosableGroups: ChoosableSubscriptionFrequencyGroup[]): IOption[] {
    const groupOptions: IOption[] = [];

    choosableGroups.forEach((group) => {
        const { id, name } = group;

        groupOptions.push({ label: name, value: id.toString() });
    });

    return groupOptions;
}

export default function AdvancedSelection({
    subscriptionFrequencyInfo,
    toggleAdvanceCallback
}: {
    subscriptionFrequencyInfo: SubscriptionFrequencyInfo;
    toggleAdvanceCallback: () => void;
}): JSX.Element {
    const { choosable } = subscriptionFrequencyInfo;
    const { selectedFrequency, setSelectedFrequency } = useContext(SelectedFrequencyContext);
    const options = getFormattedGroupOptions(choosable);
    const [selectedGroup, setSelectedGroup] = useState(getSelectedGroup(choosable));

    const { t } = useTranslation();

    useEffect(() => {
        setSelectedFrequency(getSelectedItem(selectedGroup.frequencies));
    }, [selectedGroup, setSelectedFrequency]);

    return (
        <Wrapper>
            <SelectionWrapper>
                <SelectionContainer>
                    <Typography.Body as="div">{t(Frequency)}</Typography.Body>
                    <StyledSelect
                        options={options}
                        onChange={(newGroup) => {
                            const newItem = choosable.find(
                                (group) => group.id.toString() === newGroup.value
                            ) as ChoosableSubscriptionFrequencyGroup;
                            setSelectedGroup(newItem);
                        }}
                        value={{ label: selectedGroup.name, value: selectedGroup.id.toString() }}
                    />
                </SelectionContainer>
                <SelectionContainer>
                    <Typography.Body as="div">{t(Every)}</Typography.Body>
                    <StyledSelect
                        options={mapOptionsData(selectedGroup.frequencies)}
                        onChange={(newGroup) => {
                            const frequency = getFrequencyFromId(selectedGroup.frequencies, newGroup.value);

                            setSelectedFrequency(frequency);
                        }}
                        value={{ label: selectedFrequency.name, value: selectedFrequency.id.toString() }}
                    />
                </SelectionContainer>
            </SelectionWrapper>
            <SubLine>
                <Explainer>{format(t(FrequencyExplanation))(selectedFrequency.intervalName)}</Explainer>
                <InlineButton onClick={toggleAdvanceCallback} fontWeight="semibold">
                    {t(DefaultIntervals)}
                </InlineButton>
            </SubLine>
        </Wrapper>
    );
}
